import { format, isToday, isYesterday } from "date-fns";
import type { ChatMessage } from "../annotations/WhatsAppAnnotations";
import NewPdfIcon from "static/images/NewPdfIcon";
import DocumentFileIcon from "static/images/DocumentFileIcon";
import ExcelIcon from "static/images/ExcelIcon";
import TextFileIcon from "static/images/TextFileIcon";
import DocumentPreview from "static/images/DocumentPreview";

// Format timestamp into "Today", "Yesterday", or formatted date
export const formatDateLabel = (dateString: string): string => {
  const date = new Date(dateString);
  if (isToday(date)) {return "Today";}
  if (isYesterday(date)) {return "Yesterday";}
  return format(date, "dd MMM yyyy");
};

// Group messages by formatted date
export const groupMessagesByDate = (messages: ChatMessage[]) => {
  return messages.reduce<{ [key: string]: ChatMessage[] }>((acc, msg) => {
    const dateKey = formatDateLabel(msg.timestamp);
    if (!acc[dateKey]) {acc[dateKey] = [];}
    acc[dateKey].push(msg);
    return acc;
  }, {});
};

export const getExtensionFromMediaType = (mediaType: string) => {
  const map: Record<string, string> = {
    // Word
    msword: "doc",
    "vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "vnd.ms-word.document.macroEnabled.12": "docm",
    "vnd.ms-word.template.macroEnabled.12": "dotm",
    "vnd.openxmlformats-officedocument.wordprocessingml.template": "dotx",

    // Excel
    "vnd.ms-excel": "xls",
    "vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "vnd.ms-excel.sheet.macroEnabled.12": "xlsm",
    "vnd.openxmlformats-officedocument.spreadsheetml.template": "xltx",
    "vnd.ms-excel.template.macroEnabled.12": "xltm",

    // PowerPoint
    "vnd.ms-powerpoint": "ppt",
    "vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
    "vnd.openxmlformats-officedocument.presentationml.template": "potx",
    "vnd.openxmlformats-officedocument.presentationml.slideshow": "ppsx",
    "vnd.ms-powerpoint.slideshow.macroEnabled.12": "ppsm",
    "vnd.ms-powerpoint.presentation.macroEnabled.12": "pptm",

    // Common formats
    pdf: "pdf",
    csv: "csv",
    json: "json",
    xml: "xml",
    zip: "zip",
    gzip: "gz",
    "x-7z-compressed": "7z",
    "x-rar-compressed": "rar",
  };

  if (mediaType.startsWith("text/")) {
    return "txt";
  }

  const parts = mediaType.split("/");
  const subtype = parts[1];
  return map[subtype] || subtype;
};

export const renderFileIcon = (mediaType: string) => {
  const extension = getExtensionFromMediaType(mediaType);

  switch (extension) {
    case "pdf":
      return NewPdfIcon;
    case "doc":
    case "docx":
    case "docm":
    case "dotm":
    case "dotx":
      return DocumentFileIcon;
    case "xls":
    case "xlsx":
    case "xlsm":
    case "xltx":
    case "xltm":
      return ExcelIcon;
    case "txt":
      return TextFileIcon;
    default:
      return DocumentPreview;
  }
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  } else if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${bytes} B`;
  }
};
